import { useState } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/Motion/PageTransition';

/**
 * Format currency with Belgian Euro format and K/M/B/T suffixes
 * Uses 2 decimal points and Belgian locale formatting
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  const absValue = Math.abs(value);
  let suffix = "";
  let scale = 1;

  // Determine suffix and scale
  if (absValue >= 1e12) {
    suffix = "T";
    scale = 1e12;
  } else if (absValue >= 1e9) {
    suffix = "B";
    scale = 1e9;
  } else if (absValue >= 1e6) {
    suffix = "M";
    scale = 1e6;
  } else if (absValue >= 1e3) {
    suffix = "K";
    scale = 1e3;
  }

  const scaledValue = value / scale;

  // Format with Belgian locale (period for thousands, comma for decimals)
  const formatted = new Intl.NumberFormat('fr-BE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(scaledValue);

  return `€ ${formatted}${suffix}`;
};

/**
 * LiveDashboard - Interactive Dashboard with Main and Matrix views
 */
function LiveDashboard() {
  const [activeView, setActiveView] = useState('main');
  const [activeNav, setActiveNav] = useState('Evolution');

  const navItems = [
    'Evolution',
    'Waterfalls',
    'Distribution',
    'Trends'
  ];

  const handleNavClick = (navItem) => {
    setActiveNav(navItem);
  };

  return (
    <PageTransition>
      <div className="w-full h-[calc(100vh-80px)] relative bg-white overflow-hidden">
        {/* Gradient Background */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            background: 'linear-gradient(270deg, #9ED3FF 0%, #046EA6 50%, #075985 100%)'
          }}
        />

        {/* Main Content Container */}
        <div
          className="absolute left-4 top-4 right-4 bottom-4 bg-[#F3F4F8] overflow-hidden"
          style={{ borderRadius: '32px' }}
        >
          {/* Top Header Bar */}
          <div
            className="w-full h-[60px] bg-white flex items-center px-6 gap-4"
            style={{ borderTopLeftRadius: '32px', borderTopRightRadius: '32px' }}
          >
            {/* Main Dashboard Button */}
            <button
              onClick={() => setActiveView('main')}
              className="px-6 py-2 rounded-2xl text-[17px] font-bold transition-all"
              style={{
                background: activeView === 'main' ? '#0C4A6E' : 'white',
                color: activeView === 'main' ? 'white' : '#0C4A6E',
                fontFamily: 'Aptos'
              }}
            >
              Main Dashboard
            </button>

            {/* Matrix Dashboard Button */}
            <button
              onClick={() => setActiveView('matrix')}
              className="px-6 py-2 rounded-2xl text-[17px] font-bold transition-all border-2"
              style={{
                background: activeView === 'matrix' ? '#0C4A6E' : 'white',
                color: activeView === 'matrix' ? 'white' : '#0C4A6E',
                borderColor: 'transparent',
                fontFamily: 'Aptos'
              }}
            >
              Matrix Dashboard
            </button>
          </div>

          {/* Main Content Area */}
          <div className="relative w-full" style={{ height: 'calc(100% - 60px)' }}>
            {/* Conditional rendering based on activeView */}
            {activeView === 'main' && (
            <>
            {/* Left Sidebar Navigation */}
            <div
              className="absolute left-3 top-3 w-[180px] bg-white overflow-hidden"
              style={{
                height: 'calc(100% - 24px)',
                borderRadius: '24px'
              }}
            >
              <div className="p-3 space-y-7">
                {navItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavClick(item)}
                    className="w-full text-left px-3 py-2 text-[16px] transition-all"
                    style={{
                      background: activeNav === item ? '#0C4A6E' : 'transparent',
                      color: activeNav === item ? 'white' : '#0C4A6E',
                      borderTopRightRadius: '16px',
                      borderBottomRightRadius: '16px',
                      fontFamily: 'Aptos'
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Content Area */}
            <div className="absolute left-[200px] top-3 right-3 bottom-3 overflow-hidden">
              {/* Top Stats Row - All 5 KPIs in ONE row */}
              <div className="grid gap-3 mb-3" style={{ height: '140px', minHeight: '140px', maxHeight: '140px', gridTemplateColumns: '1fr 1fr 1fr 0.64fr 0.64fr' }}>
                {/* Restaurant EBITDA */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="text-white text-center text-[16px] font-bold mb-2" style={{ fontFamily: 'Aptos' }}>
                    Restaurant EBITDA
                  </div>
                  <div className="text-white text-center text-[36px] font-bold mb-3" style={{ fontFamily: 'Aptos', lineHeight: '1' }}>
                    {formatCurrency(2847500)}
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Net Revenue</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(12500000)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Gross Margin</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(5750000)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Expenses</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(2902500)}</div>
                    </div>
                  </div>
                </motion.div>

                {/* Franchise EBITDA */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="text-white text-center text-[16px] font-bold mb-2" style={{ fontFamily: 'Aptos' }}>
                    Franchise EBITDA
                  </div>
                  <div className="text-white text-center text-[36px] font-bold mb-3" style={{ fontFamily: 'Aptos', lineHeight: '1' }}>
                    {formatCurrency(1425000)}
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Net Revenue</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(3250000)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Gross Margin</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(2600000)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Expenses</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(1175000)}</div>
                    </div>
                  </div>
                </motion.div>

                {/* Restaurant + Franchise EBITDA */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="text-white text-center text-[16px] font-bold mb-2" style={{ fontFamily: 'Aptos' }}>
                    Restaurant + Franchise EBITDA
                  </div>
                  <div className="text-white text-center text-[36px] font-bold mb-3" style={{ fontFamily: 'Aptos', lineHeight: '1' }}>
                    {formatCurrency(4272500)}
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Net Revenue</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(15750000)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Gross Margin</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(8350000)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white text-[11px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>Expenses</div>
                      <div className="text-white text-[14px] font-bold" style={{ fontFamily: 'Aptos' }}>{formatCurrency(4077500)}</div>
                    </div>
                  </div>
                </motion.div>

                {/* Overhead */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="text-white text-center text-[14px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                    Overhead
                  </div>
                  <div className="text-white text-center text-[26px] font-bold mb-2" style={{ fontFamily: 'Aptos', lineHeight: '1' }}>
                    {formatCurrency(1895000)}
                  </div>
                  {/* Sparkline - Monthly Overhead Trend */}
                  <div className="flex-1 flex items-end justify-center px-8">
                    <svg width="100%" height="45" className="overflow-visible" viewBox="0 0 120 45" preserveAspectRatio="none">
                      <polyline
                        points="5,28 15,32 25,25 35,30 45,22 55,26 65,20 75,24 85,18 95,15 105,12 115,16"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                      <circle cx="105" cy="12" r="4" fill="#C7E2C0" />
                      <circle cx="15" cy="32" r="4" fill="#EBB7B5" />
                    </svg>
                  </div>
                  <div className="text-white text-center text-[8px] opacity-70 mt-1" style={{ fontFamily: 'Aptos' }}>
                    12 Month Trend
                  </div>
                </motion.div>

                {/* Overall Net */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="text-white text-center text-[14px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                    Over All Net
                  </div>
                  <div className="text-white text-center text-[26px] font-bold mb-2" style={{ fontFamily: 'Aptos', lineHeight: '1' }}>
                    {formatCurrency(2377500)}
                  </div>
                  {/* Sparkline - Monthly Net Profit Trend */}
                  <div className="flex-1 flex items-end justify-center px-8">
                    <svg width="100%" height="45" className="overflow-visible" viewBox="0 0 120 45" preserveAspectRatio="none">
                      <polyline
                        points="5,35 15,30 25,33 35,28 45,25 55,29 65,22 75,20 85,24 95,18 105,15 115,12"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                      <circle cx="115" cy="12" r="4" fill="#C7E2C0" />
                      <circle cx="5" cy="35" r="4" fill="#EBB7B5" />
                    </svg>
                  </div>
                  <div className="text-white text-center text-[8px] opacity-70 mt-1" style={{ fontFamily: 'Aptos' }}>
                    12 Month Trend
                  </div>
                </motion.div>
              </div>

              {/* Conditional rendering based on active navigation */}
              {activeNav === 'Evolution' && (
              <div className="grid grid-cols-2 gap-3" style={{ height: 'calc(100% - 140px - 12px - 12px)', minHeight: 'calc(100% - 140px - 12px - 12px)', maxHeight: 'calc(100% - 140px - 12px - 12px)' }}>
                {/* Chart/Content Area 1 */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  {/* Title - Upper Left */}
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Revenue Evolution
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 600 240" preserveAspectRatio="xMidYMid meet">
                    {/* Grid lines - removed topmost line */}
                    <line x1="50" y1="190" x2="580" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                    <line x1="50" y1="140" x2="580" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                    <line x1="50" y1="90" x2="580" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                    <line x1="50" y1="40" x2="580" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                    {/* Y-axis labels - Revenue (left) */}
                    <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                    <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0,5M</text>
                    <text x="10" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€1M</text>
                    <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€1,5M</text>

                    {/* Y-axis labels - Evolution % (right) */}
                    <text x="585" y="195" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">-5%</text>
                    <text x="590" y="145" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">5%</text>
                    <text x="585" y="95" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">15%</text>
                    <text x="585" y="45" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">25%</text>

                    {/* Revenue Columns */}
                    <rect x="70" y="115" width="30" height="75" fill="#0C4A6E" />
                    <rect x="110" y="110" width="30" height="80" fill="#0C4A6E" />
                    <rect x="150" y="120" width="30" height="70" fill="#0C4A6E" />
                    <rect x="190" y="105" width="30" height="85" fill="#0C4A6E" />
                    <rect x="230" y="95" width="30" height="95" fill="#0C4A6E" />
                    <rect x="270" y="100" width="30" height="90" fill="#0C4A6E" />
                    <rect x="310" y="85" width="30" height="105" fill="#0C4A6E" />
                    <rect x="350" y="90" width="30" height="100" fill="#0C4A6E" />
                    <rect x="390" y="75" width="30" height="115" fill="#0C4A6E" />
                    <rect x="430" y="70" width="30" height="120" fill="#0C4A6E" />
                    <rect x="470" y="80" width="30" height="110" fill="#0C4A6E" />
                    <rect x="510" y="65" width="30" height="125" fill="#0C4A6E" />

                    {/* Data labels on columns */}
                    <text x="77" y="110" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,95M</text>
                    <text x="117" y="105" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,02M</text>
                    <text x="157" y="115" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,89M</text>
                    <text x="197" y="100" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,08M</text>
                    <text x="237" y="90" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,21M</text>
                    <text x="277" y="95" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,15M</text>
                    <text x="317" y="80" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,34M</text>
                    <text x="357" y="85" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,27M</text>
                    <text x="397" y="70" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,46M</text>
                    <text x="437" y="65" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,53M</text>
                    <text x="477" y="75" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,40M</text>
                    <text x="517" y="60" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">1,59M</text>

                    {/* Revenue Evolution % Line */}
                    <polyline
                      points="85,165 125,155 165,170 205,145 245,130 285,135 325,110 365,115 405,90 445,80 485,95 525,70"
                      fill="none"
                      stroke="#ECC846"
                      strokeWidth="2"
                    />

                    {/* X-axis labels */}
                    <text x="78" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Jan</text>
                    <text x="118" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Feb</text>
                    <text x="158" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Mar</text>
                    <text x="198" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Apr</text>
                    <text x="238" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">May</text>
                    <text x="278" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Jun</text>
                    <text x="319" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Jul</text>
                    <text x="358" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Aug</text>
                    <text x="398" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Sep</text>
                    <text x="438" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Oct</text>
                    <text x="478" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Nov</text>
                    <text x="518" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Dec</text>

                    {/* Legend */}
                    <rect x="220" y="225" width="14" height="10" fill="#0C4A6E" />
                    <text x="238" y="233" fontSize="10" fill="#6B7280" fontFamily="Aptos">Revenue</text>
                    <line x1="310" y1="230" x2="330" y2="230" stroke="#ECC846" strokeWidth="2" />
                    <text x="335" y="233" fontSize="10" fill="#6B7280" fontFamily="Aptos">Evolution %</text>
                  </svg>
                  </div>
                </motion.div>

                {/* Chart/Content Area 2 - Revenue Cumulative */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  {/* Title - Upper Left */}
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Revenue Cumulative
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 600 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="50" y1="190" x2="580" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="50" y1="140" x2="580" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="50" y1="90" x2="580" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="50" y1="40" x2="580" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Y-axis labels */}
                      <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                      <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€50M</text>
                      <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€100M</text>
                      <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€150M</text>

                      {/* Bullet Chart Bars for each year - Vertical */}
                      {/* 2021 - Inside bar lower than outside */}
                      <rect x="70" y="65" width="70" height="125" fill="#0C4A6E" rx="2" />
                      <rect x="93.5" y="85" width="23" height="105" fill="#ECC846" rx="2" />
                      <text x="92" y="77" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€128M</text>
                      <text x="87" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2021</text>

                      {/* 2022 - Inside bar HIGHER than outside */}
                      <rect x="160" y="60" width="70" height="130" fill="#0C4A6E" rx="2" />
                      <rect x="183.5" y="55" width="23" height="135" fill="#ECC846" rx="2" />
                      <text x="182" y="47" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€142M</text>
                      <text x="177" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2022</text>

                      {/* 2023 - Inside bar lower than outside */}
                      <rect x="250" y="48" width="70" height="142" fill="#0C4A6E" rx="2" />
                      <rect x="273.5" y="62" width="23" height="128" fill="#ECC846" rx="2" />
                      <text x="272" y="54" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€156M</text>
                      <text x="267" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2023</text>

                      {/* 2024 - Inside bar HIGHER than outside */}
                      <rect x="340" y="45" width="70" height="145" fill="#0C4A6E" rx="2" />
                      <rect x="363.5" y="38" width="23" height="152" fill="#ECC846" rx="2" />
                      <text x="362" y="30" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€170M</text>
                      <text x="357" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2024</text>

                      {/* 2025 - Inside bar lower than outside */}
                      <rect x="430" y="35" width="70" height="155" fill="#0C4A6E" rx="2" />
                      <rect x="453.5" y="45" width="23" height="145" fill="#ECC846" rx="2" />
                      <text x="452" y="37" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€180M</text>
                      <text x="447" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2025</text>

                      {/* Legend */}
                      <rect x="200" y="225" width="12" height="8" fill="#0C4A6E" rx="1" />
                      <text x="215" y="232" fontSize="10" fill="#6B7280" fontFamily="Aptos">Revenue PY</text>
                      <rect x="310" y="225" width="12" height="8" fill="#ECC846" rx="1" />
                      <text x="325" y="232" fontSize="10" fill="#6B7280" fontFamily="Aptos">Revenue</text>
                    </svg>
                  </div>
                </motion.div>

                {/* Chart/Content Area 3 */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  {/* Title - Upper Left */}
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    COGS Evolution
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 600 240" preserveAspectRatio="xMidYMid meet">
                    {/* Grid lines - removed topmost line */}
                    <line x1="50" y1="190" x2="580" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                    <line x1="50" y1="140" x2="580" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                    <line x1="50" y1="90" x2="580" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                    <line x1="50" y1="40" x2="580" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                    {/* Y-axis labels - COGS (left) */}
                    <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                    <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0,4M</text>
                    <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0,8M</text>
                    <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€1,2M</text>

                    {/* Y-axis labels - Evolution % (right) */}
                    <text x="585" y="195" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">-10%</text>
                    <text x="590" y="145" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">0%</text>
                    <text x="585" y="95" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">10%</text>
                    <text x="585" y="45" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">20%</text>

                    {/* COGS Columns */}
                    <rect x="70" y="80" width="30" height="110" fill="#0C4A6E" />
                    <rect x="110" y="85" width="30" height="105" fill="#0C4A6E" />
                    <rect x="150" y="95" width="30" height="95" fill="#0C4A6E" />
                    <rect x="190" y="90" width="30" height="100" fill="#0C4A6E" />
                    <rect x="230" y="100" width="30" height="90" fill="#0C4A6E" />
                    <rect x="270" y="105" width="30" height="85" fill="#0C4A6E" />
                    <rect x="310" y="110" width="30" height="80" fill="#0C4A6E" />
                    <rect x="350" y="115" width="30" height="75" fill="#0C4A6E" />
                    <rect x="390" y="120" width="30" height="70" fill="#0C4A6E" />
                    <rect x="430" y="125" width="30" height="65" fill="#0C4A6E" />
                    <rect x="470" y="130" width="30" height="60" fill="#0C4A6E" />
                    <rect x="510" y="135" width="30" height="55" fill="#0C4A6E" />

                    {/* Data labels on columns */}
                    <text x="74" y="75" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,88M</text>
                    <text x="114" y="80" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,84M</text>
                    <text x="154" y="90" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,76M</text>
                    <text x="194" y="85" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,80M</text>
                    <text x="234" y="95" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,72M</text>
                    <text x="274" y="100" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,68M</text>
                    <text x="314" y="105" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,64M</text>
                    <text x="354" y="110" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,60M</text>
                    <text x="394" y="115" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,56M</text>
                    <text x="434" y="120" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,52M</text>
                    <text x="474" y="125" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,48M</text>
                    <text x="514" y="130" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">0,44M</text>

                    {/* COGS Evolution % Line */}
                    <polyline
                      points="85,180 125,170 165,175 205,160 245,155 285,150 325,140 365,145 405,130 445,125 485,135 525,115"
                      fill="none"
                      stroke="#ECC846"
                      strokeWidth="2"
                    />

                    {/* X-axis labels */}
                    <text x="78" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Jan</text>
                    <text x="118" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Feb</text>
                    <text x="158" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Mar</text>
                    <text x="198" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Apr</text>
                    <text x="238" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">May</text>
                    <text x="278" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Jun</text>
                    <text x="319" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Jul</text>
                    <text x="358" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Aug</text>
                    <text x="398" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Sep</text>
                    <text x="438" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Oct</text>
                    <text x="478" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Nov</text>
                    <text x="518" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">Dec</text>

                    {/* Legend */}
                    <rect x="230" y="225" width="14" height="10" fill="#0C4A6E" />
                    <text x="248" y="233" fontSize="10" fill="#6B7280" fontFamily="Aptos">COGS</text>
                    <line x1="300" y1="230" x2="320" y2="230" stroke="#ECC846" strokeWidth="2" />
                    <text x="325" y="233" fontSize="10" fill="#6B7280" fontFamily="Aptos">Evolution %</text>
                  </svg>
                  </div>
                </motion.div>

                {/* Chart/Content Area 4 - COGS Cumulative */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  {/* Title - Upper Left */}
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    COGS Cumulative
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 600 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="50" y1="190" x2="580" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="50" y1="140" x2="580" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="50" y1="90" x2="580" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="50" y1="40" x2="580" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Y-axis labels */}
                      <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                      <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€30M</text>
                      <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€60M</text>
                      <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€90M</text>

                      {/* Bullet Chart Bars for each year - Vertical */}
                      {/* 2021 - Inside bar HIGHER than outside */}
                      <rect x="70" y="75" width="70" height="115" fill="#0C4A6E" rx="2" />
                      <rect x="93.5" y="70" width="23" height="120" fill="#ECC846" rx="2" />
                      <text x="92" y="62" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€72M</text>
                      <text x="87" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2021</text>

                      {/* 2022 - Inside bar lower than outside */}
                      <rect x="160" y="80" width="70" height="110" fill="#0C4A6E" rx="2" />
                      <rect x="183.5" y="100" width="23" height="90" fill="#ECC846" rx="2" />
                      <text x="182" y="92" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€68M</text>
                      <text x="177" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2022</text>

                      {/* 2023 - Inside bar HIGHER than outside */}
                      <rect x="250" y="95" width="70" height="95" fill="#0C4A6E" rx="2" />
                      <rect x="273.5" y="88" width="23" height="102" fill="#ECC846" rx="2" />
                      <text x="272" y="80" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€64M</text>
                      <text x="267" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2023</text>

                      {/* 2024 - Inside bar lower than outside */}
                      <rect x="340" y="100" width="70" height="90" fill="#0C4A6E" rx="2" />
                      <rect x="363.5" y="120" width="23" height="70" fill="#ECC846" rx="2" />
                      <text x="362" y="112" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€60M</text>
                      <text x="357" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2024</text>

                      {/* 2025 - Inside bar HIGHER than outside */}
                      <rect x="430" y="115" width="70" height="75" fill="#0C4A6E" rx="2" />
                      <rect x="453.5" y="108" width="23" height="82" fill="#ECC846" rx="2" />
                      <text x="452" y="100" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€56M</text>
                      <text x="447" y="210" fontSize="11" fill="#6B7280" fontFamily="Aptos">2025</text>

                      {/* Legend */}
                      <rect x="210" y="225" width="12" height="8" fill="#0C4A6E" rx="1" />
                      <text x="225" y="232" fontSize="10" fill="#6B7280" fontFamily="Aptos">COGS PY</text>
                      <rect x="310" y="225" width="12" height="8" fill="#ECC846" rx="1" />
                      <text x="325" y="232" fontSize="10" fill="#6B7280" fontFamily="Aptos">COGS</text>
                    </svg>
                  </div>
                </motion.div>
              </div>
              )}

              {/* Waterfalls View */}
              {activeNav === 'Waterfalls' && (
              <div className="grid grid-cols-2 gap-3" style={{ height: 'calc(100% - 140px - 12px - 12px)', minHeight: 'calc(100% - 140px - 12px - 12px)', maxHeight: 'calc(100% - 140px - 12px - 12px)' }}>
                {/* Restaurant Revenue Breakdown */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Restaurant Revenue Breakdown
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="120" y1="190" x2="630" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="140" x2="630" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="40" x2="630" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Y-axis labels */}
                      <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                      <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€5M</text>
                      <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€10M</text>
                      <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€15M</text>

                      {/* Waterfall bars */}
                      {/* Starting Total */}
                      <rect x="130" y="40" width="50" height="150" fill="#0C4A6E" rx="2" />
                      <text x="140" y="32" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€15.2M</text>
                      <text x="140" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Start</text>

                      {/* Product Sales (70 Account) */}
                      <line x1="180" y1="100" x2="200" y2="100" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="200" y="100" width="50" height="90" fill="#ECC846" rx="2" />
                      <text x="208" y="92" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€9.0M</text>
                      <text x="205" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Sales</text>

                      {/* Service Revenue (74 Account) */}
                      <line x1="250" y1="100" x2="270" y2="100" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="270" y="70" width="50" height="30" fill="#ECC846" rx="2" />
                      <text x="278" y="62" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€3.2M</text>
                      <text x="275" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Service</text>

                      {/* Other Revenue (76 Account) */}
                      <line x1="320" y1="70" x2="340" y2="70" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="340" y="50" width="50" height="20" fill="#ECC846" rx="2" />
                      <text x="348" y="42" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.0M</text>
                      <text x="348" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Other</text>

                      {/* Discounts (77 Account - Negative) */}
                      <line x1="390" y1="50" x2="410" y2="50" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="410" y="50" width="50" height="30" fill="#E74C3C" rx="2" />
                      <text x="418" y="42" fontSize="9" fill="#E74C3C" fontFamily="Aptos" fontWeight="bold">-€1.0M</text>
                      <text x="415" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Discount</text>

                      {/* Returns (78 Account - Negative) */}
                      <line x1="460" y1="80" x2="480" y2="80" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="480" y="80" width="50" height="20" fill="#E74C3C" rx="2" />
                      <text x="488" y="72" fontSize="9" fill="#E74C3C" fontFamily="Aptos" fontWeight="bold">-€0.8M</text>
                      <text x="488" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Returns</text>

                      {/* Net Total */}
                      <line x1="530" y1="100" x2="550" y2="100" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="550" y="40" width="50" height="150" fill="#0C4A6E" rx="2" />
                      <text x="558" y="32" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€15.6M</text>
                      <text x="565" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Net</text>

                      {/* Legend */}
                      <rect x="180" y="225" width="12" height="8" fill="#ECC846" rx="1" />
                      <text x="195" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Increase</text>
                      <rect x="260" y="225" width="12" height="8" fill="#E74C3C" rx="1" />
                      <text x="275" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Decrease</text>
                      <rect x="340" y="225" width="12" height="8" fill="#0C4A6E" rx="1" />
                      <text x="355" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Total</text>
                    </svg>
                  </div>
                </motion.div>

                {/* Restaurant COGS Breakdown */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Restaurant COGS Breakdown
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="120" y1="190" x2="630" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="140" x2="630" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="40" x2="630" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Y-axis labels */}
                      <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                      <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€3M</text>
                      <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€6M</text>
                      <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€9M</text>

                      {/* Waterfall bars */}
                      {/* Starting Total */}
                      <rect x="130" y="70" width="50" height="120" fill="#0C4A6E" rx="2" />
                      <text x="140" y="62" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€8.2M</text>
                      <text x="140" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Start</text>

                      {/* Raw Materials (60 Account) */}
                      <line x1="180" y1="130" x2="200" y2="130" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="200" y="130" width="50" height="60" fill="#ECC846" rx="2" />
                      <text x="208" y="122" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€4.0M</text>
                      <text x="205" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Materials</text>

                      {/* Direct Labor (62 Account) */}
                      <line x1="250" y1="130" x2="270" y2="130" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="270" y="100" width="50" height="30" fill="#ECC846" rx="2" />
                      <text x="278" y="92" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.1M</text>
                      <text x="278" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Labor</text>

                      {/* Manufacturing OH (64 Account) */}
                      <line x1="320" y1="100" x2="340" y2="100" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="340" y="80" width="50" height="20" fill="#ECC846" rx="2" />
                      <text x="348" y="72" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.4M</text>
                      <text x="350" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Overhead</text>

                      {/* Freight In (61 Account) */}
                      <line x1="390" y1="80" x2="410" y2="80" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="410" y="70" width="50" height="10" fill="#ECC846" rx="2" />
                      <text x="418" y="62" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.7M</text>
                      <text x="418" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Freight</text>

                      {/* Inventory Adjust (Negative) */}
                      <line x1="460" y1="70" x2="480" y2="70" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="480" y="70" width="50" height="10" fill="#E74C3C" rx="2" />
                      <text x="488" y="62" fontSize="9" fill="#E74C3C" fontFamily="Aptos" fontWeight="bold">-€0.6M</text>
                      <text x="488" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Adjust</text>

                      {/* Net Total */}
                      <line x1="530" y1="80" x2="550" y2="80" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="550" y="70" width="50" height="120" fill="#0C4A6E" rx="2" />
                      <text x="558" y="62" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€7.6M</text>
                      <text x="565" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Net</text>

                      {/* Legend */}
                      <rect x="180" y="225" width="12" height="8" fill="#ECC846" rx="1" />
                      <text x="195" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Increase</text>
                      <rect x="260" y="225" width="12" height="8" fill="#E74C3C" rx="1" />
                      <text x="275" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Decrease</text>
                      <rect x="340" y="225" width="12" height="8" fill="#0C4A6E" rx="1" />
                      <text x="355" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Total</text>
                    </svg>
                  </div>
                </motion.div>

                {/* Franchise Revenue Breakdown */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Franchise Revenue Breakdown
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="120" y1="190" x2="630" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="140" x2="630" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="40" x2="630" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Y-axis labels */}
                      <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                      <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€2M</text>
                      <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€4M</text>
                      <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€6M</text>

                      {/* Waterfall bars */}
                      {/* Starting Total */}
                      <rect x="130" y="90" width="50" height="100" fill="#0C4A6E" rx="2" />
                      <text x="140" y="82" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€4.8M</text>
                      <text x="140" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Start</text>

                      {/* Franchise Fees (70 Account) */}
                      <line x1="180" y1="140" x2="200" y2="140" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="200" y="140" width="50" height="50" fill="#ECC846" rx="2" />
                      <text x="208" y="132" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.5M</text>
                      <text x="215" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Fees</text>

                      {/* Royalties (74 Account) */}
                      <line x1="250" y1="140" x2="270" y2="140" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="270" y="120" width="50" height="20" fill="#ECC846" rx="2" />
                      <text x="278" y="112" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.0M</text>
                      <text x="275" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Royalty</text>

                      {/* Marketing Fees (76 Account) */}
                      <line x1="320" y1="120" x2="340" y2="120" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="340" y="105" width="50" height="15" fill="#ECC846" rx="2" />
                      <text x="348" y="97" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.7M</text>
                      <text x="345" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Marketing</text>

                      {/* Training Revenue (77 Account) */}
                      <line x1="390" y1="105" x2="410" y2="105" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="410" y="95" width="50" height="10" fill="#ECC846" rx="2" />
                      <text x="418" y="87" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.5M</text>
                      <text x="418" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Training</text>

                      {/* Fee Adjustments (Negative) */}
                      <line x1="460" y1="95" x2="480" y2="95" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="480" y="95" width="50" height="10" fill="#E74C3C" rx="2" />
                      <text x="488" y="87" fontSize="9" fill="#E74C3C" fontFamily="Aptos" fontWeight="bold">-€0.2M</text>
                      <text x="488" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Adjust</text>

                      {/* Net Total */}
                      <line x1="530" y1="105" x2="550" y2="105" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="550" y="90" width="50" height="100" fill="#0C4A6E" rx="2" />
                      <text x="558" y="82" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€4.5M</text>
                      <text x="565" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Net</text>

                      {/* Legend */}
                      <rect x="180" y="225" width="12" height="8" fill="#ECC846" rx="1" />
                      <text x="195" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Increase</text>
                      <rect x="260" y="225" width="12" height="8" fill="#E74C3C" rx="1" />
                      <text x="275" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Decrease</text>
                      <rect x="340" y="225" width="12" height="8" fill="#0C4A6E" rx="1" />
                      <text x="355" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Total</text>
                    </svg>
                  </div>
                </motion.div>

                {/* Franchise COGS Breakdown */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Franchise COGS Breakdown
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="120" y1="190" x2="630" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="140" x2="630" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="120" y1="40" x2="630" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Y-axis labels */}
                      <text x="10" y="195" fontSize="10" fill="#6B7280" fontFamily="Aptos">€0M</text>
                      <text x="5" y="145" fontSize="10" fill="#6B7280" fontFamily="Aptos">€1M</text>
                      <text x="5" y="95" fontSize="10" fill="#6B7280" fontFamily="Aptos">€2M</text>
                      <text x="5" y="45" fontSize="10" fill="#6B7280" fontFamily="Aptos">€3M</text>

                      {/* Waterfall bars */}
                      {/* Starting Total */}
                      <rect x="130" y="110" width="50" height="80" fill="#0C4A6E" rx="2" />
                      <text x="140" y="102" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.8M</text>
                      <text x="140" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Start</text>

                      {/* Support Services (60 Account) */}
                      <line x1="180" y1="150" x2="200" y2="150" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="200" y="150" width="50" height="40" fill="#ECC846" rx="2" />
                      <text x="208" y="142" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.2M</text>
                      <text x="210" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Support</text>

                      {/* Marketing Costs (62 Account) */}
                      <line x1="250" y1="150" x2="270" y2="150" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="270" y="135" width="50" height="15" fill="#ECC846" rx="2" />
                      <text x="278" y="127" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.6M</text>
                      <text x="275" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Marketing</text>

                      {/* Training Costs (64 Account) */}
                      <line x1="320" y1="135" x2="340" y2="135" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="340" y="125" width="50" height="10" fill="#ECC846" rx="2" />
                      <text x="348" y="117" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.4M</text>
                      <text x="348" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Training</text>

                      {/* Technology (66 Account) */}
                      <line x1="390" y1="125" x2="410" y2="125" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="410" y="115" width="50" height="10" fill="#ECC846" rx="2" />
                      <text x="418" y="107" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.3M</text>
                      <text x="420" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Tech</text>

                      {/* Cost Recoveries (Negative) */}
                      <line x1="460" y1="115" x2="480" y2="115" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="480" y="115" width="50" height="5" fill="#E74C3C" rx="2" />
                      <text x="488" y="107" fontSize="9" fill="#E74C3C" fontFamily="Aptos" fontWeight="bold">-€0.1M</text>
                      <text x="488" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Recovery</text>

                      {/* Net Total */}
                      <line x1="530" y1="120" x2="550" y2="120" stroke="#0C4A6E" strokeWidth="1" strokeDasharray="3,3" />
                      <rect x="550" y="110" width="50" height="80" fill="#0C4A6E" rx="2" />
                      <text x="558" y="102" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.4M</text>
                      <text x="565" y="210" fontSize="9" fill="#6B7280" fontFamily="Aptos">Net</text>

                      {/* Legend */}
                      <rect x="180" y="225" width="12" height="8" fill="#ECC846" rx="1" />
                      <text x="195" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Increase</text>
                      <rect x="260" y="225" width="12" height="8" fill="#E74C3C" rx="1" />
                      <text x="275" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Decrease</text>
                      <rect x="340" y="225" width="12" height="8" fill="#0C4A6E" rx="1" />
                      <text x="355" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Total</text>
                    </svg>
                  </div>
                </motion.div>
              </div>
              )}

              {/* Distribution View */}
              {activeNav === 'Distribution' && (
              <div className="grid grid-cols-2 gap-3" style={{ height: 'calc(100% - 140px - 12px - 12px)', minHeight: 'calc(100% - 140px - 12px - 12px)', maxHeight: 'calc(100% - 140px - 12px - 12px)' }}>
                {/* Sales by Restaurant - Donut Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Sales by Restaurant
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 500 260" preserveAspectRatio="xMidYMid meet">
                      {/* Donut Chart - Restaurant Sales */}
                      <g transform="translate(250, 95)">
                        {/* Le Bistro Moderne - 28% - Top */}
                        <path d="M 0,-70 A 70,70 0 0,1 59.41,-36.29 L 42.59,-26.03 A 50,50 0 0,0 0,-50 Z" fill="#0C4A6E" />
                        {/* Connector line */}
                        <line x1="30" y1="-53" x2="50" y2="-80" stroke="#0C4A6E" strokeWidth="1.5" />
                        <text x="52" y="-76" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="600">Le Bistro Moderne</text>

                        {/* La Brasserie du Nord - 24% - Right */}
                        <path d="M 59.41,-36.29 A 70,70 0 0,1 67.82,16.89 L 48.44,12.07 A 50,50 0 0,0 42.59,-26.03 Z" fill="#ECC846" />
                        {/* Connector line */}
                        <line x1="63" y1="-10" x2="85" y2="-15" stroke="#ECC846" strokeWidth="1.5" />
                        <text x="87" y="-11" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">La Brasserie du Nord</text>

                        {/* Chez Flamme - 22% - Bottom Right */}
                        <path d="M 67.82,16.89 A 70,70 0 0,1 22.50,68.13 L 16.07,48.66 A 50,50 0 0,0 48.44,12.07 Z" fill="#94C5E8" />
                        {/* Connector line */}
                        <line x1="45" y1="55" x2="65" y2="80" stroke="#94C5E8" strokeWidth="1.5" />
                        <text x="67" y="84" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">Chez Flamme</text>

                        {/* Le Jardin Secret - 16% - Bottom Left */}
                        <path d="M 22.50,68.13 A 70,70 0 0,1 -45.01,55.00 L -32.15,39.29 A 50,50 0 0,0 16.07,48.66 Z" fill="#F5A623" />
                        {/* Connector line */}
                        <line x1="-34" y1="47" x2="-60" y2="70" stroke="#F5A623" strokeWidth="1.5" />
                        <text x="-145" y="74" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">Le Jardin Secret</text>

                        {/* La Table Royale - 10% - Left */}
                        <path d="M -45.01,55.00 A 70,70 0 0,1 0,-70 L 0,-50 A 50,50 0 0,0 -32.15,39.29 Z" fill="#7B68EE" />
                        {/* Connector line */}
                        <line x1="-38" y1="0" x2="-70" y2="0" stroke="#7B68EE" strokeWidth="1.5" />
                        <text x="-155" y="4" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">La Table Royale</text>

                        {/* Center circle (donut hole) */}
                        <circle cx="0" cy="0" r="50" fill="white" />

                        {/* Center text - PERCENTAGE */}
                        <text x="0" y="10" fontSize="24" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold" textAnchor="middle">28%</text>
                      </g>
                    </svg>
                  </div>
                </motion.div>

                {/* Sales by Franchise - Donut Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Sales by Franchise
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 500 260" preserveAspectRatio="xMidYMid meet">
                      {/* Donut Chart - Franchise Sales */}
                      <g transform="translate(250, 95)">
                        {/* Saveur Express - 32% - Top */}
                        <path d="M 0,-70 A 70,70 0 0,1 66.69,-25.22 L 47.64,-18.01 A 50,50 0 0,0 0,-50 Z" fill="#0C4A6E" />
                        {/* Connector line */}
                        <line x1="33" y1="-48" x2="55" y2="-75" stroke="#0C4A6E" strokeWidth="1.5" />
                        <text x="57" y="-71" fontSize="9" fill="#0C4A6E" fontFamily="Aptos" fontWeight="600">Saveur Express</text>

                        {/* Le Coin Gourmand - 26% - Right */}
                        <path d="M 66.69,-25.22 A 70,70 0 0,1 42.48,57.53 L 30.34,41.09 A 50,50 0 0,0 47.64,-18.01 Z" fill="#ECC846" />
                        {/* Connector line */}
                        <line x1="53" y1="16" x2="78" y2="25" stroke="#ECC846" strokeWidth="1.5" />
                        <text x="80" y="29" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">Le Coin Gourmand</text>

                        {/* La Petite Cuisine - 21% - Bottom Right */}
                        <path d="M 42.48,57.53 A 70,70 0 0,1 -33.29,62.19 L -23.78,44.42 A 50,50 0 0,0 30.34,41.09 Z" fill="#94C5E8" />
                        {/* Connector line */}
                        <line x1="5" y1="60" x2="10" y2="85" stroke="#94C5E8" strokeWidth="1.5" />
                        <text x="12" y="89" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">La Petite Cuisine</text>

                        {/* Délices du Quartier - 14% - Bottom Left */}
                        <path d="M -33.29,62.19 A 70,70 0 0,1 -66.69,25.22 L -47.64,18.01 A 50,50 0 0,0 -23.78,44.42 Z" fill="#F5A623" />
                        {/* Connector line */}
                        <line x1="-50" y1="41" x2="-80" y2="60" stroke="#F5A623" strokeWidth="1.5" />
                        <text x="-168" y="64" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">Délices du Quartier</text>

                        {/* Brasserie Rapide - 7% - Left */}
                        <path d="M -66.69,25.22 A 70,70 0 0,1 0,-70 L 0,-50 A 50,50 0 0,0 -47.64,18.01 Z" fill="#7B68EE" />
                        {/* Connector line */}
                        <line x1="-47" y1="-18" x2="-75" y2="-30" stroke="#7B68EE" strokeWidth="1.5" />
                        <text x="-165" y="-26" fontSize="9" fill="#6B7280" fontFamily="Aptos" fontWeight="600">Brasserie Rapide</text>

                        {/* Center circle (donut hole) */}
                        <circle cx="0" cy="0" r="50" fill="white" />

                        {/* Center text - PERCENTAGE */}
                        <text x="0" y="10" fontSize="24" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold" textAnchor="middle">32%</text>
                      </g>
                    </svg>
                  </div>
                </motion.div>

                {/* Expenses - Bar Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Expenses
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="200" y1="190" x2="630" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="200" y1="140" x2="630" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="200" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="200" y1="40" x2="630" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Horizontal Bar Chart */}
                      {/* Salaries & Wages - HIGHEST */}
                      <rect x="200" y="35" width="320" height="22" fill="#E74C3C" rx="2" />
                      <text x="525" y="50" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.8M</text>
                      <text x="10" y="50" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">Salaries & Wages</text>

                      {/* Rent & Lease */}
                      <rect x="200" y="62" width="240" height="22" fill="#0C4A6E" rx="2" />
                      <text x="445" y="77" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€2.1M</text>
                      <text x="10" y="77" fontSize="10" fill="#6B7280" fontFamily="Aptos">Rent & Lease</text>

                      {/* Marketing */}
                      <rect x="200" y="89" width="180" height="22" fill="#0C4A6E" rx="2" />
                      <text x="385" y="104" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.6M</text>
                      <text x="10" y="104" fontSize="10" fill="#6B7280" fontFamily="Aptos">Marketing</text>

                      {/* Utilities */}
                      <rect x="200" y="116" width="145" height="22" fill="#0C4A6E" rx="2" />
                      <text x="350" y="131" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.3M</text>
                      <text x="10" y="131" fontSize="10" fill="#6B7280" fontFamily="Aptos">Utilities</text>

                      {/* Insurance */}
                      <rect x="200" y="143" width="110" height="22" fill="#0C4A6E" rx="2" />
                      <text x="315" y="158" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.0M</text>
                      <text x="10" y="158" fontSize="10" fill="#6B7280" fontFamily="Aptos">Insurance</text>

                      {/* Professional Fees */}
                      <rect x="200" y="170" width="85" height="22" fill="#0C4A6E" rx="2" />
                      <text x="290" y="185" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.8M</text>
                      <text x="10" y="185" fontSize="10" fill="#6B7280" fontFamily="Aptos">Professional Fees</text>

                      {/* Legend */}
                      <rect x="480" y="225" width="12" height="8" fill="#E74C3C" rx="1" />
                      <text x="495" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Highest Expense</text>
                    </svg>
                  </div>
                </motion.div>

                {/* Overheads - Bar Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Overheads
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="200" y1="190" x2="630" y2="190" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="200" y1="140" x2="630" y2="140" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="200" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="200" y1="40" x2="630" y2="40" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Horizontal Bar Chart */}
                      {/* Administrative Salaries - HIGHEST */}
                      <rect x="200" y="35" width="280" height="22" fill="#E74C3C" rx="2" />
                      <text x="485" y="50" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.4M</text>
                      <text x="10" y="50" fontSize="10" fill="#0C4A6E" fontFamily="Aptos">Administrative Salaries</text>

                      {/* Depreciation */}
                      <rect x="200" y="62" width="200" height="22" fill="#0C4A6E" rx="2" />
                      <text x="405" y="77" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€1.0M</text>
                      <text x="10" y="77" fontSize="10" fill="#6B7280" fontFamily="Aptos">Depreciation</text>

                      {/* IT & Technology */}
                      <rect x="200" y="89" width="160" height="22" fill="#0C4A6E" rx="2" />
                      <text x="365" y="104" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.8M</text>
                      <text x="10" y="104" fontSize="10" fill="#6B7280" fontFamily="Aptos">IT & Technology</text>

                      {/* Office Supplies */}
                      <rect x="200" y="116" width="120" height="22" fill="#0C4A6E" rx="2" />
                      <text x="325" y="131" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.6M</text>
                      <text x="10" y="131" fontSize="10" fill="#6B7280" fontFamily="Aptos">Office Supplies</text>

                      {/* Legal & Compliance */}
                      <rect x="200" y="143" width="80" height="22" fill="#0C4A6E" rx="2" />
                      <text x="285" y="158" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.4M</text>
                      <text x="10" y="158" fontSize="10" fill="#6B7280" fontFamily="Aptos">Legal & Compliance</text>

                      {/* Bank Charges */}
                      <rect x="200" y="170" width="60" height="22" fill="#0C4A6E" rx="2" />
                      <text x="265" y="185" fontSize="10" fill="#0C4A6E" fontFamily="Aptos" fontWeight="bold">€0.3M</text>
                      <text x="10" y="185" fontSize="10" fill="#6B7280" fontFamily="Aptos">Bank Charges</text>

                      {/* Legend */}
                      <rect x="480" y="225" width="12" height="8" fill="#E74C3C" rx="1" />
                      <text x="495" y="232" fontSize="9" fill="#6B7280" fontFamily="Aptos">Highest Overhead</text>
                    </svg>
                  </div>
                </motion.div>
              </div>
              )}

              {/* Trends View */}
              {activeNav === 'Trends' && (
              <div className="grid grid-cols-2 gap-3" style={{ height: 'calc(100% - 140px - 12px - 12px)', minHeight: 'calc(100% - 140px - 12px - 12px)', maxHeight: 'calc(100% - 140px - 12px - 12px)' }}>
                {/* Sales by Restaurant - Area Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Sales by Restaurant
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="60" y1="180" x2="630" y2="180" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="135" x2="630" y2="135" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="45" x2="630" y2="45" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Area Charts - Stacked */}
                      {/* Le Bistro Moderne - Bottom Layer */}
                      <path d="M 80,160 L 127,155 L 174,158 L 221,152 L 268,148 L 315,150 L 362,145 L 409,147 L 456,142 L 503,140 L 550,143 L 597,138 L 597,180 L 80,180 Z"
                            fill="#0C4A6E" opacity="0.7" />

                      {/* La Brasserie du Nord - Second Layer */}
                      <path d="M 80,160 L 127,155 L 174,158 L 221,152 L 268,148 L 315,150 L 362,145 L 409,147 L 456,142 L 503,140 L 550,143 L 597,138
                               L 597,125 L 550,128 L 503,122 L 456,120 L 409,123 L 362,118 L 315,120 L 268,115 L 221,117 L 174,120 L 127,118 L 80,122 Z"
                            fill="#ECC846" opacity="0.7" />

                      {/* Chez Flamme - Third Layer */}
                      <path d="M 80,122 L 127,118 L 174,120 L 221,117 L 268,115 L 315,120 L 362,118 L 409,123 L 456,120 L 503,122 L 550,128 L 597,125
                               L 597,108 L 550,110 L 503,105 L 456,102 L 409,105 L 362,100 L 315,102 L 268,97 L 221,99 L 174,102 L 127,100 L 80,104 Z"
                            fill="#94C5E8" opacity="0.7" />

                      {/* Le Jardin Secret - Fourth Layer */}
                      <path d="M 80,104 L 127,100 L 174,102 L 221,99 L 268,97 L 315,102 L 362,100 L 409,105 L 456,102 L 503,105 L 550,110 L 597,108
                               L 597,88 L 550,90 L 503,85 L 456,82 L 409,85 L 362,80 L 315,82 L 268,77 L 221,79 L 174,82 L 127,80 L 80,84 Z"
                            fill="#F5A623" opacity="0.7" />

                      {/* La Table Royale - Top Layer */}
                      <path d="M 80,84 L 127,80 L 174,82 L 221,79 L 268,77 L 315,82 L 362,80 L 409,85 L 456,82 L 503,85 L 550,90 L 597,88
                               L 597,60 L 550,62 L 503,57 L 456,54 L 409,57 L 362,52 L 315,54 L 268,49 L 221,51 L 174,54 L 127,52 L 80,56 Z"
                            fill="#7B68EE" opacity="0.7" />

                      {/* X-axis labels */}
                      <text x="73" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jan</text>
                      <text x="120" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Feb</text>
                      <text x="167" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Mar</text>
                      <text x="214" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Apr</text>
                      <text x="261" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">May</text>
                      <text x="308" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jun</text>
                      <text x="356" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jul</text>
                      <text x="402" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Aug</text>
                      <text x="449" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Sep</text>
                      <text x="496" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Oct</text>
                      <text x="543" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Nov</text>
                      <text x="590" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Dec</text>

                      {/* Legend */}
                      <g transform="translate(80, 215)">
                        <rect x="0" y="0" width="10" height="10" fill="#0C4A6E" opacity="0.7" rx="2" />
                        <text x="14" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Le Bistro Moderne</text>

                        <rect x="125" y="0" width="10" height="10" fill="#ECC846" opacity="0.7" rx="2" />
                        <text x="139" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">La Brasserie du Nord</text>

                        <rect x="265" y="0" width="10" height="10" fill="#94C5E8" opacity="0.7" rx="2" />
                        <text x="279" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Chez Flamme</text>

                        <rect x="360" y="0" width="10" height="10" fill="#F5A623" opacity="0.7" rx="2" />
                        <text x="374" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Le Jardin Secret</text>

                        <rect x="465" y="0" width="10" height="10" fill="#7B68EE" opacity="0.7" rx="2" />
                        <text x="479" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">La Table Royale</text>
                      </g>
                    </svg>
                  </div>
                </motion.div>

                {/* Sales by Franchise - Area Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Sales by Franchise
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="60" y1="180" x2="630" y2="180" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="135" x2="630" y2="135" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="45" x2="630" y2="45" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Area Charts - Stacked */}
                      {/* Saveur Express - Bottom Layer (32%) */}
                      <path d="M 80,135 L 127,130 L 174,133 L 221,127 L 268,123 L 315,125 L 362,120 L 409,122 L 456,117 L 503,115 L 550,118 L 597,113 L 597,180 L 80,180 Z"
                            fill="#0C4A6E" opacity="0.7" />

                      {/* Le Coin Gourmand - Second Layer (26%) */}
                      <path d="M 80,135 L 127,130 L 174,133 L 221,127 L 268,123 L 315,125 L 362,120 L 409,122 L 456,117 L 503,115 L 550,118 L 597,113
                               L 597,95 L 550,97 L 503,92 L 456,89 L 409,92 L 362,87 L 315,89 L 268,84 L 221,86 L 174,89 L 127,87 L 80,91 Z"
                            fill="#ECC846" opacity="0.7" />

                      {/* La Petite Cuisine - Third Layer (21%) */}
                      <path d="M 80,91 L 127,87 L 174,89 L 221,86 L 268,84 L 315,89 L 362,87 L 409,92 L 456,89 L 503,92 L 550,97 L 597,95
                               L 597,75 L 550,77 L 503,72 L 456,69 L 409,72 L 362,67 L 315,69 L 268,64 L 221,66 L 174,69 L 127,67 L 80,71 Z"
                            fill="#94C5E8" opacity="0.7" />

                      {/* Délices du Quartier - Fourth Layer (14%) */}
                      <path d="M 80,71 L 127,67 L 174,69 L 221,66 L 268,64 L 315,69 L 362,67 L 409,72 L 456,69 L 503,72 L 550,77 L 597,75
                               L 597,60 L 550,62 L 503,57 L 456,54 L 409,57 L 362,52 L 315,54 L 268,49 L 221,51 L 174,54 L 127,52 L 80,56 Z"
                            fill="#F5A623" opacity="0.7" />

                      {/* Brasserie Rapide - Top Layer (7%) */}
                      <path d="M 80,56 L 127,52 L 174,54 L 221,51 L 268,49 L 315,54 L 362,52 L 409,57 L 456,54 L 503,57 L 550,62 L 597,60
                               L 597,48 L 550,50 L 503,45 L 456,42 L 409,45 L 362,40 L 315,42 L 268,37 L 221,39 L 174,42 L 127,40 L 80,44 Z"
                            fill="#7B68EE" opacity="0.7" />

                      {/* X-axis labels */}
                      <text x="73" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jan</text>
                      <text x="120" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Feb</text>
                      <text x="167" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Mar</text>
                      <text x="214" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Apr</text>
                      <text x="261" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">May</text>
                      <text x="308" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jun</text>
                      <text x="356" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jul</text>
                      <text x="402" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Aug</text>
                      <text x="449" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Sep</text>
                      <text x="496" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Oct</text>
                      <text x="543" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Nov</text>
                      <text x="590" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Dec</text>

                      {/* Legend */}
                      <g transform="translate(100, 215)">
                        <rect x="0" y="0" width="10" height="10" fill="#0C4A6E" opacity="0.7" rx="2" />
                        <text x="14" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Saveur Express</text>

                        <rect x="110" y="0" width="10" height="10" fill="#ECC846" opacity="0.7" rx="2" />
                        <text x="124" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Le Coin Gourmand</text>

                        <rect x="235" y="0" width="10" height="10" fill="#94C5E8" opacity="0.7" rx="2" />
                        <text x="249" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">La Petite Cuisine</text>

                        <rect x="360" y="0" width="10" height="10" fill="#F5A623" opacity="0.7" rx="2" />
                        <text x="374" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Délices du Quartier</text>
                      </g>
                    </svg>
                  </div>
                </motion.div>

                {/* Expenses - Area Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Expenses
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="60" y1="180" x2="630" y2="180" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="135" x2="630" y2="135" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="45" x2="630" y2="45" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Area Charts - Stacked */}
                      {/* Salaries & Wages - Bottom Layer (Largest) */}
                      <path d="M 80,130 L 127,125 L 174,128 L 221,122 L 268,118 L 315,120 L 362,115 L 409,117 L 456,112 L 503,110 L 550,113 L 597,108 L 597,180 L 80,180 Z"
                            fill="#E74C3C" opacity="0.7" />

                      {/* Rent & Lease - Second Layer */}
                      <path d="M 80,130 L 127,125 L 174,128 L 221,122 L 268,118 L 315,120 L 362,115 L 409,117 L 456,112 L 503,110 L 550,113 L 597,108
                               L 597,90 L 550,92 L 503,87 L 456,84 L 409,87 L 362,82 L 315,84 L 268,79 L 221,81 L 174,84 L 127,82 L 80,86 Z"
                            fill="#0C4A6E" opacity="0.6" />

                      {/* Marketing - Third Layer */}
                      <path d="M 80,86 L 127,82 L 174,84 L 221,81 L 268,79 L 315,84 L 362,82 L 409,87 L 456,84 L 503,87 L 550,92 L 597,90
                               L 597,75 L 550,77 L 503,72 L 456,69 L 409,72 L 362,67 L 315,69 L 268,64 L 221,66 L 174,69 L 127,67 L 80,71 Z"
                            fill="#ECC846" opacity="0.6" />

                      {/* Utilities - Fourth Layer */}
                      <path d="M 80,71 L 127,67 L 174,69 L 221,66 L 268,64 L 315,69 L 362,67 L 409,72 L 456,69 L 503,72 L 550,77 L 597,75
                               L 597,62 L 550,64 L 503,59 L 456,56 L 409,59 L 362,54 L 315,56 L 268,51 L 221,53 L 174,56 L 127,54 L 80,58 Z"
                            fill="#94C5E8" opacity="0.6" />

                      {/* Insurance - Fifth Layer */}
                      <path d="M 80,58 L 127,54 L 174,56 L 221,53 L 268,51 L 315,56 L 362,54 L 409,59 L 456,56 L 503,59 L 550,64 L 597,62
                               L 597,52 L 550,54 L 503,49 L 456,46 L 409,49 L 362,44 L 315,46 L 268,41 L 221,43 L 174,46 L 127,44 L 80,48 Z"
                            fill="#F5A623" opacity="0.6" />

                      {/* Professional Fees - Top Layer */}
                      <path d="M 80,48 L 127,44 L 174,46 L 221,43 L 268,41 L 315,46 L 362,44 L 409,49 L 456,46 L 503,49 L 550,54 L 597,52
                               L 597,45 L 550,47 L 503,42 L 456,39 L 409,42 L 362,37 L 315,39 L 268,34 L 221,36 L 174,39 L 127,37 L 80,41 Z"
                            fill="#7B68EE" opacity="0.6" />

                      {/* X-axis labels */}
                      <text x="73" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jan</text>
                      <text x="120" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Feb</text>
                      <text x="167" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Mar</text>
                      <text x="214" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Apr</text>
                      <text x="261" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">May</text>
                      <text x="308" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jun</text>
                      <text x="356" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jul</text>
                      <text x="402" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Aug</text>
                      <text x="449" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Sep</text>
                      <text x="496" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Oct</text>
                      <text x="543" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Nov</text>
                      <text x="590" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Dec</text>

                      {/* Legend */}
                      <g transform="translate(50, 215)">
                        <rect x="0" y="0" width="10" height="10" fill="#E74C3C" opacity="0.7" rx="2" />
                        <text x="14" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Salaries & Wages</text>

                        <rect x="115" y="0" width="10" height="10" fill="#0C4A6E" opacity="0.6" rx="2" />
                        <text x="129" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Rent & Lease</text>

                        <rect x="205" y="0" width="10" height="10" fill="#ECC846" opacity="0.6" rx="2" />
                        <text x="219" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Marketing</text>

                        <rect x="280" y="0" width="10" height="10" fill="#94C5E8" opacity="0.6" rx="2" />
                        <text x="294" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Utilities</text>

                        <rect x="345" y="0" width="10" height="10" fill="#F5A623" opacity="0.6" rx="2" />
                        <text x="359" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Insurance</text>

                        <rect x="415" y="0" width="10" height="10" fill="#7B68EE" opacity="0.6" rx="2" />
                        <text x="429" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Professional Fees</text>
                      </g>
                    </svg>
                  </div>
                </motion.div>

                {/* Overheads - Area Chart */}
                <motion.div
                  className="bg-white rounded-[24px] p-4 flex flex-col overflow-hidden relative"
                  style={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', maxWidth: '100%' }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  <div className="absolute top-4 left-6 text-[#0C4A6E] text-[16px] font-bold" style={{ fontFamily: 'Aptos' }}>
                    Overheads
                  </div>

                  <div className="flex-1 flex items-center justify-center">
                    <svg width="100%" height="100%" viewBox="0 0 650 240" preserveAspectRatio="xMidYMid meet">
                      {/* Grid lines */}
                      <line x1="60" y1="180" x2="630" y2="180" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="135" x2="630" y2="135" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="90" x2="630" y2="90" stroke="#E5E7EB" strokeWidth="1" />
                      <line x1="60" y1="45" x2="630" y2="45" stroke="#E5E7EB" strokeWidth="1" />

                      {/* Area Charts - Stacked */}
                      {/* Administrative Salaries - Bottom Layer (Largest) */}
                      <path d="M 80,115 L 127,110 L 174,113 L 221,107 L 268,103 L 315,105 L 362,100 L 409,102 L 456,97 L 503,95 L 550,98 L 597,93 L 597,180 L 80,180 Z"
                            fill="#E74C3C" opacity="0.7" />

                      {/* Depreciation - Second Layer */}
                      <path d="M 80,115 L 127,110 L 174,113 L 221,107 L 268,103 L 315,105 L 362,100 L 409,102 L 456,97 L 503,95 L 550,98 L 597,93
                               L 597,82 L 550,84 L 503,79 L 456,76 L 409,79 L 362,74 L 315,76 L 268,71 L 221,73 L 174,76 L 127,74 L 80,78 Z"
                            fill="#0C4A6E" opacity="0.6" />

                      {/* IT & Technology - Third Layer */}
                      <path d="M 80,78 L 127,74 L 174,76 L 221,73 L 268,71 L 315,76 L 362,74 L 409,79 L 456,76 L 503,79 L 550,84 L 597,82
                               L 597,68 L 550,70 L 503,65 L 456,62 L 409,65 L 362,60 L 315,62 L 268,57 L 221,59 L 174,62 L 127,60 L 80,64 Z"
                            fill="#ECC846" opacity="0.6" />

                      {/* Office Supplies - Fourth Layer */}
                      <path d="M 80,64 L 127,60 L 174,62 L 221,59 L 268,57 L 315,62 L 362,60 L 409,65 L 456,62 L 503,65 L 550,70 L 597,68
                               L 597,58 L 550,60 L 503,55 L 456,52 L 409,55 L 362,50 L 315,52 L 268,47 L 221,49 L 174,52 L 127,50 L 80,54 Z"
                            fill="#94C5E8" opacity="0.6" />

                      {/* Legal & Compliance - Fifth Layer */}
                      <path d="M 80,54 L 127,50 L 174,52 L 221,49 L 268,47 L 315,52 L 362,50 L 409,55 L 456,52 L 503,55 L 550,60 L 597,58
                               L 597,50 L 550,52 L 503,47 L 456,44 L 409,47 L 362,42 L 315,44 L 268,39 L 221,41 L 174,44 L 127,42 L 80,46 Z"
                            fill="#F5A623" opacity="0.6" />

                      {/* Bank Charges - Top Layer */}
                      <path d="M 80,46 L 127,42 L 174,44 L 221,41 L 268,39 L 315,44 L 362,42 L 409,47 L 456,44 L 503,47 L 550,52 L 597,50
                               L 597,45 L 550,47 L 503,42 L 456,39 L 409,42 L 362,37 L 315,39 L 268,34 L 221,36 L 174,39 L 127,37 L 80,41 Z"
                            fill="#7B68EE" opacity="0.6" />

                      {/* X-axis labels */}
                      <text x="73" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jan</text>
                      <text x="120" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Feb</text>
                      <text x="167" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Mar</text>
                      <text x="214" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Apr</text>
                      <text x="261" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">May</text>
                      <text x="308" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jun</text>
                      <text x="356" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Jul</text>
                      <text x="402" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Aug</text>
                      <text x="449" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Sep</text>
                      <text x="496" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Oct</text>
                      <text x="543" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Nov</text>
                      <text x="590" y="200" fontSize="10" fill="#6B7280" fontFamily="Aptos">Dec</text>

                      {/* Legend */}
                      <g transform="translate(40, 215)">
                        <rect x="0" y="0" width="10" height="10" fill="#E74C3C" opacity="0.7" rx="2" />
                        <text x="14" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Admin Salaries</text>

                        <rect x="105" y="0" width="10" height="10" fill="#0C4A6E" opacity="0.6" rx="2" />
                        <text x="119" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Depreciation</text>

                        <rect x="195" y="0" width="10" height="10" fill="#ECC846" opacity="0.6" rx="2" />
                        <text x="209" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">IT & Technology</text>

                        <rect x="300" y="0" width="10" height="10" fill="#94C5E8" opacity="0.6" rx="2" />
                        <text x="314" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Office Supplies</text>

                        <rect x="400" y="0" width="10" height="10" fill="#F5A623" opacity="0.6" rx="2" />
                        <text x="414" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Legal & Compliance</text>

                        <rect x="520" y="0" width="10" height="10" fill="#7B68EE" opacity="0.6" rx="2" />
                        <text x="534" y="9" fontSize="9" fill="#6B7280" fontFamily="Aptos">Bank Charges</text>
                      </g>
                    </svg>
                  </div>
                </motion.div>
              </div>
              )}
            </div>
            </>
            )}

            {/* Matrix Dashboard View */}
            {activeView === 'matrix' && (
            <div className="absolute left-3 top-3 right-3 bottom-3 overflow-hidden">
              {/* Top Stats Row - Same 5 KPIs */}
              <div className="grid gap-3 mb-3" style={{ height: '140px', minHeight: '140px', maxHeight: '140px', gridTemplateColumns: '1fr 1fr 1fr 0.64fr 0.64fr' }}>
                {/* Restaurant EBITDA */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="text-center">
                    <div className="text-white text-[16px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      Restaurant EBITDA
                    </div>
                    <div className="text-white text-[28px] font-bold mb-2" style={{ fontFamily: 'Aptos' }}>
                      € 2,85M
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-white text-[11px]" style={{ fontFamily: 'Aptos' }}>
                      <div>
                        <div className="font-semibold">Net Revenue</div>
                        <div className="font-bold text-[13px]">€ 12,50M</div>
                      </div>
                      <div>
                        <div className="font-semibold">Gross Margin</div>
                        <div className="font-bold text-[13px]">€ 5,75M</div>
                      </div>
                      <div>
                        <div className="font-semibold">Expenses</div>
                        <div className="font-bold text-[13px]">€ 2,90M</div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Franchise EBITDA */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="text-center">
                    <div className="text-white text-[16px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      Franchise EBITDA
                    </div>
                    <div className="text-white text-[28px] font-bold mb-2" style={{ fontFamily: 'Aptos' }}>
                      € 1,43M
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-white text-[11px]" style={{ fontFamily: 'Aptos' }}>
                      <div>
                        <div className="font-semibold">Net Revenue</div>
                        <div className="font-bold text-[13px]">€ 3,25M</div>
                      </div>
                      <div>
                        <div className="font-semibold">Gross Margin</div>
                        <div className="font-bold text-[13px]">€ 2,60M</div>
                      </div>
                      <div>
                        <div className="font-semibold">Expenses</div>
                        <div className="font-bold text-[13px]">€ 1,18M</div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Restaurant + Franchise EBITDA */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="text-center">
                    <div className="text-white text-[16px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      Restaurant + Franchise EBITDA
                    </div>
                    <div className="text-white text-[28px] font-bold mb-2" style={{ fontFamily: 'Aptos' }}>
                      € 4,27M
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-white text-[11px]" style={{ fontFamily: 'Aptos' }}>
                      <div>
                        <div className="font-semibold">Net Revenue</div>
                        <div className="font-bold text-[13px]">€ 15,75M</div>
                      </div>
                      <div>
                        <div className="font-semibold">Gross Margin</div>
                        <div className="font-bold text-[13px]">€ 8,35M</div>
                      </div>
                      <div>
                        <div className="font-semibold">Expenses</div>
                        <div className="font-bold text-[13px]">€ 4,08M</div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Overhead */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-between"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="text-center">
                    <div className="text-white text-[14px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      Overhead
                    </div>
                    <div className="text-white text-[22px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      € 1,90M
                    </div>
                  </div>
                  <svg width="100%" height="40" viewBox="0 0 180 40" preserveAspectRatio="none">
                    <polyline
                      points="5,30 20,28 35,32 50,27 65,25 80,29 95,24 110,26 125,22 140,20 155,23 170,19"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    <circle cx="140" cy="20" r="3" fill="#C7E2C0" />
                    <circle cx="170" cy="19" r="3" fill="#EBB7B5" />
                  </svg>
                  <div className="text-white text-[9px] text-center mt-1" style={{ fontFamily: 'Aptos' }}>
                    12 Month Trend
                  </div>
                </motion.div>

                {/* Over All Net */}
                <motion.div
                  className="bg-[#0C4A6E] rounded-[20px] p-2.5 flex flex-col justify-between"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="text-center">
                    <div className="text-white text-[14px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      Over All Net
                    </div>
                    <div className="text-white text-[22px] font-bold mb-1" style={{ fontFamily: 'Aptos' }}>
                      € 2,38M
                    </div>
                  </div>
                  <svg width="100%" height="40" viewBox="0 0 180 40" preserveAspectRatio="none">
                    <polyline
                      points="5,32 20,30 35,28 50,31 65,27 80,25 95,29 110,24 125,26 140,23 155,21 170,24"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                    <circle cx="155" cy="21" r="3" fill="#C7E2C0" />
                    <circle cx="65" cy="27" r="3" fill="#EBB7B5" />
                  </svg>
                  <div className="text-white text-[9px] text-center mt-1" style={{ fontFamily: 'Aptos' }}>
                    12 Month Trend
                  </div>
                </motion.div>
              </div>

              {/* Matrix Table */}
              <motion.div
                className="bg-white rounded-[24px] overflow-hidden"
                style={{ height: 'calc(100% - 152px)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="h-full overflow-auto">
                  <table className="w-full" style={{ fontFamily: 'Aptos', fontSize: '13px' }}>
                    <thead className="sticky top-0 bg-[#0C4A6E] text-white z-10">
                      <tr>
                        <th className="text-left py-3 px-4 font-bold border-r border-white/20" style={{ width: '280px' }}>Account Description</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Jan</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Feb</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Mar</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Apr</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>May</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Jun</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Jul</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Aug</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Sep</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Oct</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Nov</th>
                        <th className="text-right py-3 px-3 font-bold border-r border-white/20" style={{ width: '110px' }}>Dec</th>
                        <th className="text-right py-3 px-4 font-bold bg-[#075985]" style={{ width: '130px' }}>YTD Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* REVENUE SECTION */}
                      <tr className="bg-[#0C4A6E] text-white font-bold">
                        <td colSpan="14" className="py-2.5 px-4 text-[15px]">REVENUE</td>
                      </tr>

                      {/* Restaurant Revenue */}
                      <tr className="bg-[#EBF5FB] hover:bg-[#D6EAF8] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Restaurant Revenue</td>
                        <td className="text-right py-2 px-3">€ 1,25M</td>
                        <td className="text-right py-2 px-3">€ 1,28M</td>
                        <td className="text-right py-2 px-3">€ 1,22M</td>
                        <td className="text-right py-2 px-3">€ 1,30M</td>
                        <td className="text-right py-2 px-3">€ 1,27M</td>
                        <td className="text-right py-2 px-3">€ 1,31M</td>
                        <td className="text-right py-2 px-3">€ 1,29M</td>
                        <td className="text-right py-2 px-3">€ 1,33M</td>
                        <td className="text-right py-2 px-3">€ 1,28M</td>
                        <td className="text-right py-2 px-3">€ 1,35M</td>
                        <td className="text-right py-2 px-3">€ 1,32M</td>
                        <td className="text-right py-2 px-3">€ 1,36M</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#D6EAF8]">€ 15,56M</td>
                      </tr>

                      {/* Franchise Revenue */}
                      <tr className="bg-white hover:bg-[#F8F9FA] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Franchise Revenue</td>
                        <td className="text-right py-2 px-3">€ 265K</td>
                        <td className="text-right py-2 px-3">€ 272K</td>
                        <td className="text-right py-2 px-3">€ 268K</td>
                        <td className="text-right py-2 px-3">€ 275K</td>
                        <td className="text-right py-2 px-3">€ 271K</td>
                        <td className="text-right py-2 px-3">€ 278K</td>
                        <td className="text-right py-2 px-3">€ 274K</td>
                        <td className="text-right py-2 px-3">€ 281K</td>
                        <td className="text-right py-2 px-3">€ 277K</td>
                        <td className="text-right py-2 px-3">€ 284K</td>
                        <td className="text-right py-2 px-3">€ 280K</td>
                        <td className="text-right py-2 px-3">€ 287K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#F0F0F0]">€ 3,31M</td>
                      </tr>

                      {/* Total Revenue */}
                      <tr className="bg-[#0C4A6E]/10 font-bold border-t-2 border-[#0C4A6E]">
                        <td className="py-2.5 px-4 pl-6 text-[#0C4A6E]">Total Revenue</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,52M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,55M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,49M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,58M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,54M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,59M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,56M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,61M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,56M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,63M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,60M</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 1,65M</td>
                        <td className="text-right py-2.5 px-4 bg-[#0C4A6E] text-white">€ 18,87M</td>
                      </tr>

                      {/* COST OF GOODS SOLD SECTION */}
                      <tr className="bg-[#0C4A6E] text-white font-bold">
                        <td colSpan="14" className="py-2.5 px-4 text-[15px]">COST OF GOODS SOLD</td>
                      </tr>

                      {/* Raw Materials */}
                      <tr className="bg-[#EBF5FB] hover:bg-[#D6EAF8] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Raw Materials</td>
                        <td className="text-right py-2 px-3">€ 425K</td>
                        <td className="text-right py-2 px-3">€ 432K</td>
                        <td className="text-right py-2 px-3">€ 420K</td>
                        <td className="text-right py-2 px-3">€ 438K</td>
                        <td className="text-right py-2 px-3">€ 429K</td>
                        <td className="text-right py-2 px-3">€ 441K</td>
                        <td className="text-right py-2 px-3">€ 435K</td>
                        <td className="text-right py-2 px-3">€ 446K</td>
                        <td className="text-right py-2 px-3">€ 433K</td>
                        <td className="text-right py-2 px-3">€ 450K</td>
                        <td className="text-right py-2 px-3">€ 442K</td>
                        <td className="text-right py-2 px-3">€ 455K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#D6EAF8]">€ 5,25M</td>
                      </tr>

                      {/* Direct Labor */}
                      <tr className="bg-white hover:bg-[#F8F9FA] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Direct Labor</td>
                        <td className="text-right py-2 px-3">€ 275K</td>
                        <td className="text-right py-2 px-3">€ 278K</td>
                        <td className="text-right py-2 px-3">€ 272K</td>
                        <td className="text-right py-2 px-3">€ 281K</td>
                        <td className="text-right py-2 px-3">€ 276K</td>
                        <td className="text-right py-2 px-3">€ 283K</td>
                        <td className="text-right py-2 px-3">€ 279K</td>
                        <td className="text-right py-2 px-3">€ 286K</td>
                        <td className="text-right py-2 px-3">€ 280K</td>
                        <td className="text-right py-2 px-3">€ 289K</td>
                        <td className="text-right py-2 px-3">€ 284K</td>
                        <td className="text-right py-2 px-3">€ 292K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#F0F0F0]">€ 3,38M</td>
                      </tr>

                      {/* Total COGS */}
                      <tr className="bg-[#0C4A6E]/10 font-bold border-t-2 border-[#0C4A6E]">
                        <td className="py-2.5 px-4 pl-6 text-[#0C4A6E]">Total COGS</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 700K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 710K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 692K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 719K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 705K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 724K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 714K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 732K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 713K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 739K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 726K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 747K</td>
                        <td className="text-right py-2.5 px-4 bg-[#0C4A6E] text-white">€ 8,62M</td>
                      </tr>

                      {/* GROSS MARGIN */}
                      <tr className="bg-[#ECC846]/20 font-bold border-t-2 border-[#ECC846]">
                        <td className="py-3 px-4 pl-4 text-[#0C4A6E] text-[14px]">GROSS MARGIN</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 820K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 840K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 798K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 861K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 835K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 866K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 846K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 878K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 847K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 891K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 874K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 903K</td>
                        <td className="text-right py-3 px-4 bg-[#ECC846] text-[#0C4A6E] font-bold">€ 10,26M</td>
                      </tr>

                      {/* OPERATING EXPENSES SECTION */}
                      <tr className="bg-[#0C4A6E] text-white font-bold">
                        <td colSpan="14" className="py-2.5 px-4 text-[15px]">OPERATING EXPENSES</td>
                      </tr>

                      {/* Salaries & Wages */}
                      <tr className="bg-[#EBF5FB] hover:bg-[#D6EAF8] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Salaries & Wages</td>
                        <td className="text-right py-2 px-3">€ 230K</td>
                        <td className="text-right py-2 px-3">€ 235K</td>
                        <td className="text-right py-2 px-3">€ 228K</td>
                        <td className="text-right py-2 px-3">€ 238K</td>
                        <td className="text-right py-2 px-3">€ 233K</td>
                        <td className="text-right py-2 px-3">€ 240K</td>
                        <td className="text-right py-2 px-3">€ 236K</td>
                        <td className="text-right py-2 px-3">€ 243K</td>
                        <td className="text-right py-2 px-3">€ 237K</td>
                        <td className="text-right py-2 px-3">€ 246K</td>
                        <td className="text-right py-2 px-3">€ 241K</td>
                        <td className="text-right py-2 px-3">€ 249K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#D6EAF8]">€ 2,86M</td>
                      </tr>

                      {/* Rent & Lease */}
                      <tr className="bg-white hover:bg-[#F8F9FA] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Rent & Lease</td>
                        <td className="text-right py-2 px-3">€ 175K</td>
                        <td className="text-right py-2 px-3">€ 175K</td>
                        <td className="text-right py-2 px-3">€ 175K</td>
                        <td className="text-right py-2 px-3">€ 175K</td>
                        <td className="text-right py-2 px-3">€ 175K</td>
                        <td className="text-right py-2 px-3">€ 175K</td>
                        <td className="text-right py-2 px-3">€ 178K</td>
                        <td className="text-right py-2 px-3">€ 178K</td>
                        <td className="text-right py-2 px-3">€ 178K</td>
                        <td className="text-right py-2 px-3">€ 178K</td>
                        <td className="text-right py-2 px-3">€ 178K</td>
                        <td className="text-right py-2 px-3">€ 178K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#F0F0F0]">€ 2,12M</td>
                      </tr>

                      {/* Marketing */}
                      <tr className="bg-[#EBF5FB] hover:bg-[#D6EAF8] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Marketing</td>
                        <td className="text-right py-2 px-3">€ 132K</td>
                        <td className="text-right py-2 px-3">€ 135K</td>
                        <td className="text-right py-2 px-3">€ 130K</td>
                        <td className="text-right py-2 px-3">€ 138K</td>
                        <td className="text-right py-2 px-3">€ 134K</td>
                        <td className="text-right py-2 px-3">€ 140K</td>
                        <td className="text-right py-2 px-3">€ 136K</td>
                        <td className="text-right py-2 px-3">€ 142K</td>
                        <td className="text-right py-2 px-3">€ 137K</td>
                        <td className="text-right py-2 px-3">€ 144K</td>
                        <td className="text-right py-2 px-3">€ 140K</td>
                        <td className="text-right py-2 px-3">€ 146K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#D6EAF8]">€ 1,65M</td>
                      </tr>

                      {/* Utilities */}
                      <tr className="bg-white hover:bg-[#F8F9FA] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Utilities</td>
                        <td className="text-right py-2 px-3">€ 105K</td>
                        <td className="text-right py-2 px-3">€ 108K</td>
                        <td className="text-right py-2 px-3">€ 102K</td>
                        <td className="text-right py-2 px-3">€ 110K</td>
                        <td className="text-right py-2 px-3">€ 106K</td>
                        <td className="text-right py-2 px-3">€ 112K</td>
                        <td className="text-right py-2 px-3">€ 115K</td>
                        <td className="text-right py-2 px-3">€ 118K</td>
                        <td className="text-right py-2 px-3">€ 110K</td>
                        <td className="text-right py-2 px-3">€ 114K</td>
                        <td className="text-right py-2 px-3">€ 108K</td>
                        <td className="text-right py-2 px-3">€ 105K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#F0F0F0]">€ 1,31M</td>
                      </tr>

                      {/* Total Operating Expenses */}
                      <tr className="bg-[#0C4A6E]/10 font-bold border-t-2 border-[#0C4A6E]">
                        <td className="py-2.5 px-4 pl-6 text-[#0C4A6E]">Total Operating Expenses</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 642K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 653K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 635K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 661K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 648K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 667K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 665K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 681K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 662K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 682K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 667K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 678K</td>
                        <td className="text-right py-2.5 px-4 bg-[#0C4A6E] text-white">€ 7,94M</td>
                      </tr>

                      {/* EBITDA */}
                      <tr className="bg-[#94C5E8]/30 font-bold border-t-2 border-[#0C4A6E] text-[14px]">
                        <td className="py-3 px-4 pl-4 text-[#0C4A6E]">EBITDA</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 178K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 187K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 163K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 200K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 187K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 199K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 181K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 197K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 185K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 209K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 207K</td>
                        <td className="text-right py-3 px-3 text-[#0C4A6E]">€ 225K</td>
                        <td className="text-right py-3 px-4 bg-[#94C5E8] text-[#0C4A6E] font-bold">€ 2,32M</td>
                      </tr>

                      {/* OVERHEAD SECTION */}
                      <tr className="bg-[#0C4A6E] text-white font-bold">
                        <td colSpan="14" className="py-2.5 px-4 text-[15px]">OVERHEAD</td>
                      </tr>

                      {/* Administrative Salaries */}
                      <tr className="bg-[#EBF5FB] hover:bg-[#D6EAF8] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Administrative Salaries</td>
                        <td className="text-right py-2 px-3">€ 115K</td>
                        <td className="text-right py-2 px-3">€ 117K</td>
                        <td className="text-right py-2 px-3">€ 114K</td>
                        <td className="text-right py-2 px-3">€ 119K</td>
                        <td className="text-right py-2 px-3">€ 116K</td>
                        <td className="text-right py-2 px-3">€ 120K</td>
                        <td className="text-right py-2 px-3">€ 118K</td>
                        <td className="text-right py-2 px-3">€ 122K</td>
                        <td className="text-right py-2 px-3">€ 119K</td>
                        <td className="text-right py-2 px-3">€ 124K</td>
                        <td className="text-right py-2 px-3">€ 121K</td>
                        <td className="text-right py-2 px-3">€ 125K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#D6EAF8]">€ 1,43M</td>
                      </tr>

                      {/* Depreciation */}
                      <tr className="bg-white hover:bg-[#F8F9FA] transition-colors">
                        <td className="py-2 px-4 pl-8 font-semibold text-[#0C4A6E]">Depreciation</td>
                        <td className="text-right py-2 px-3">€ 83K</td>
                        <td className="text-right py-2 px-3">€ 83K</td>
                        <td className="text-right py-2 px-3">€ 83K</td>
                        <td className="text-right py-2 px-3">€ 83K</td>
                        <td className="text-right py-2 px-3">€ 83K</td>
                        <td className="text-right py-2 px-3">€ 83K</td>
                        <td className="text-right py-2 px-3">€ 85K</td>
                        <td className="text-right py-2 px-3">€ 85K</td>
                        <td className="text-right py-2 px-3">€ 85K</td>
                        <td className="text-right py-2 px-3">€ 85K</td>
                        <td className="text-right py-2 px-3">€ 85K</td>
                        <td className="text-right py-2 px-3">€ 85K</td>
                        <td className="text-right py-2 px-4 font-bold bg-[#F0F0F0]">€ 1,01M</td>
                      </tr>

                      {/* Total Overhead */}
                      <tr className="bg-[#0C4A6E]/10 font-bold border-t-2 border-[#0C4A6E]">
                        <td className="py-2.5 px-4 pl-6 text-[#0C4A6E]">Total Overhead</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 198K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 200K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 197K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 202K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 199K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 203K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 203K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 207K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 204K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 209K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 206K</td>
                        <td className="text-right py-2.5 px-3 text-[#0C4A6E]">€ 210K</td>
                        <td className="text-right py-2.5 px-4 bg-[#0C4A6E] text-white">€ 2,44M</td>
                      </tr>

                      {/* NET INCOME */}
                      <tr className="bg-gradient-to-r from-[#0C4A6E] to-[#075985] text-white font-bold text-[15px] border-t-4 border-[#ECC846]">
                        <td className="py-4 px-4 pl-4">NET INCOME</td>
                        <td className="text-right py-4 px-3">€ -20K</td>
                        <td className="text-right py-4 px-3">€ -13K</td>
                        <td className="text-right py-4 px-3">€ -34K</td>
                        <td className="text-right py-4 px-3">€ -2K</td>
                        <td className="text-right py-4 px-3">€ -12K</td>
                        <td className="text-right py-4 px-3">€ -4K</td>
                        <td className="text-right py-4 px-3">€ -22K</td>
                        <td className="text-right py-4 px-3">€ -10K</td>
                        <td className="text-right py-4 px-3">€ -19K</td>
                        <td className="text-right py-4 px-3">€ 0K</td>
                        <td className="text-right py-4 px-3">€ 1K</td>
                        <td className="text-right py-4 px-3">€ 15K</td>
                        <td className="text-right py-4 px-4 bg-[#ECC846] text-[#0C4A6E] font-bold text-[16px]">€ -120K</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>
            )}
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default LiveDashboard;











